import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, QueryList, Renderer2, ViewChild, ViewChildren } from '@angular/core'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { ActivatedRoute, Router } from '@angular/router'
import { MessageService } from 'primeng/api'
import { catchError, combineLatest, forkJoin, of } from 'rxjs'

import { AuthService } from '@data/services/modules/auth/auth.service'
import { DOCUMENTATION_CATALOG, findDocumentation } from '../data/constants/documentation-catalog.constant'
import { DecisionProgress, DecisionState, DocumentationDecision, DocumentationEntry, DocumentationThread, ThreadStatus, TocEntry } from '../data/interfaces/documentation.interface'
import { DECISION_STATE_LABEL, decisionProgress, decisionState, extractDecisions } from '../data/utils/decisions.util'
import { DocumentationService } from '../data/services/api/documentation.service'

/** Aire bajo la cabecera fija al saltar a una sección. */
const HEAD_OFFSET = 90

type PanelMode = 'block' | 'all'
type ThreadFilter = 'open' | 'resolved' | 'all' | 'decisions'

@Component({
  selector: 'app-documentation-viewer',
  templateUrl: './documentation-viewer.component.html',
  styleUrls: ['./documentation-viewer.component.scss'],
})
export class DocumentationViewerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('shell') shellRef!: ElementRef<HTMLElement>
  @ViewChild('content') contentRef!: ElementRef<HTMLElement>
  @ViewChild('panelBody') panelBodyRef?: ElementRef<HTMLElement>
  @ViewChildren('card') cardRefs?: QueryList<ElementRef<HTMLElement>>

  doc: DocumentationEntry | null = null
  companion: DocumentationEntry | null = null
  catalog = DOCUMENTATION_CATALOG.filter((entry) => entry.available)
  safeHtml: SafeHtml | null = null
  toc: TocEntry[] = []
  activeSection = ''

  threads: DocumentationThread[] = []
  /** Lo que le pedimos a Contabilidad que conteste, derivado del propio asset. */
  decisions: DocumentationDecision[] = []
  loading = true
  loadError = ''
  /** El manual se pudo leer pero los comentarios no. Se avisa sin bloquear la lectura. */
  threadsError = false

  panelOpen = false
  panelMode: PanelMode = 'all'
  filter: ThreadFilter = 'open'
  activeBlockId: string | null = null
  activeBlockText = ''

  //* El índice se recoge solo al abrir los comentarios para no dejar el contenido en un
  //* carril angosto; el botón "Índice" de la cabecera lo trae de vuelta si hace falta.
  tocOpen = true
  private tocAutoHidden = false

  draft = ''
  replyDraft: Record<string, string | undefined> = {}
  editingId: string | null = null
  editDraft = ''
  saving = false

  private blockIndex = new Map<string, HTMLElement>()
  private blockOrder = new Map<string, number>()
  private badges = new Map<string, HTMLElement>()
  private decisionChips = new Map<string, HTMLElement>()
  private hoverBlock: HTMLElement | null = null
  private hoverBtn: HTMLElement | null = null
  private hideTimer: ReturnType<typeof setTimeout> | null = null
  private scrollFrame: number | null = null
  private hoverBtnBlockId: string | null = null
  //* Lo último que ESTE componente escribió en la URL. Sirve para distinguir un cambio
  //* propio (el índice siguiendo al scroll) de uno externo (atrás, adelante, un enlace
  //* pegado): sólo el externo debe mover la vista.
  private ultimoFragmentoEscrito: string | null = null
  private fragmentoPendiente: string | null = null
  private resizeObserver: ResizeObserver | null = null
  private sectionObserver: IntersectionObserver | null = null
  private unlisten: Array<() => void> = []

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly documentationService: DocumentationService,
    private readonly authService: AuthService,
    private readonly sanitizer: DomSanitizer,
    private readonly renderer: Renderer2,
    private readonly zone: NgZone,
    private readonly messageService: MessageService
  ) {}

  ngOnInit(): void {
    //* La sección viaja en el fragmento de la URL, así que recargar, compartir el enlace o
    //* usar atrás/adelante caen en el mismo lugar del documento.
    combineLatest([this.route.paramMap, this.route.fragment]).subscribe(([params, fragment]) => {
      const slug = params.get('slug') ?? ''

      if (slug !== this.doc?.slug) {
        this.fragmentoPendiente = fragment
        this.load(slug)
        return
      }

      //* Mismo documento: sólo hay que moverse si el cambio vino de fuera.
      if (fragment && fragment !== this.ultimoFragmentoEscrito) this.jumpTo(fragment)
    })
  }

  ngAfterViewInit(): void {
    //* Las tarjetas se posicionan según el bloque que comentan, así que hay que recolocarlas
    //* cada vez que cambia la lista (abrir el panel, filtrar, publicar, resolver).
    this.cardRefs?.changes.subscribe(() => setTimeout(() => this.alignThreads()))

    //* mousemove fuera de la zona: dispara decenas de veces por segundo y no debe
    //* provocar detección de cambios. Solo se reentra a la zona al abrir el panel.
    this.zone.runOutsideAngular(() => {
      this.unlisten.push(this.renderer.listen(this.shellRef.nativeElement, 'mousemove', (event: MouseEvent) => this.onHover(event)))
      this.unlisten.push(this.renderer.listen(this.shellRef.nativeElement, 'mouseleave', () => this.clearHover()))
    })

    //* Este SÍ va dentro de la zona: mueve la vista y toca el estado.
    this.unlisten.push(this.renderer.listen(this.shellRef.nativeElement, 'click', (event: MouseEvent) => this.onContentClick(event)))

    //* Al volver a la pestaña se releen los hilos: si alguien más contestó mientras tanto, el
    //* avance no puede seguir diciendo un número viejo. Se hace al recuperar el foco y no
    //* encuestando cada N segundos — una página de documentación no justifica tráfico
    //* constante, y el caso real es irse y volver.
    this.unlisten.push(this.renderer.listen('window', 'focus', () => this.refreshThreads()))
  }

  ngOnDestroy(): void {
    this.cancelHide()
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame)
    this.unlisten.forEach((off) => off())
    this.resizeObserver?.disconnect()
    this.sectionObserver?.disconnect()
  }

  // ─────────────────────────── carga ───────────────────────────

  private load(slug: string): void {
    const doc = findDocumentation(slug)
    if (!doc || !doc.available) {
      this.router.navigate(['/'])
      return
    }

    this.doc = doc
    this.companion = doc.companion ? findDocumentation(doc.companion) ?? null : null
    this.loading = true
    this.loadError = ''
    this.closePanel()
    this.resetContent()

    forkJoin({
      html: this.documentationService.getContent(doc.asset),
      //* El manual es un asset del bundle: si los comentarios no cargan, se lee igual.
      //* Sin este catch, un 403 o la API caída dejarían el documento en esqueleto para siempre.
      threads: this.documentationService.getThreads(doc.slug).pipe(catchError(() => of(null))),
    }).subscribe(({ html, threads }) => {
      this.loading = false
      this.threadsError = !threads

      if (!html) {
        this.loadError = 'No se pudo cargar el contenido del manual.'
        return
      }

      this.toc = this.buildToc(html)
      this.decisions = extractDecisions(html)
      this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(html)
      this.threads = Array.isArray(threads?.data) ? threads.data : []

      //* El contenido se pinta en el siguiente ciclo; hasta entonces no hay bloques que indexar.
      setTimeout(() => {
        this.onContentRendered()
        if (!this.fragmentoPendiente) return

        //* La sección puede no existir aquí: pasa al conmutar a un documento que todavía no
        //* tiene esa contraparte. Se aterriza arriba y se limpia el fragmento, porque una URL
        //* que apunta a una sección inexistente se compartiría rota.
        if (!this.jumpTo(this.fragmentoPendiente)) {
          this.ultimoFragmentoEscrito = ''
          this.router.navigate([], { relativeTo: this.route, fragment: undefined, replaceUrl: true })
        }
        this.fragmentoPendiente = null
      })
    })
  }

  private resetContent(): void {
    this.safeHtml = null
    this.toc = []
    this.decisions = []
    this.activeSection = ''
    this.threads = []
    this.blockIndex.clear()
    this.blockOrder.clear()
    this.clearHover()
    this.badges.forEach((el) => this.renderer.removeChild(this.shellRef?.nativeElement, el))
    this.badges.clear()
    this.decisionChips.forEach((el) => el.remove())
    this.decisionChips.clear()
    this.resizeObserver?.disconnect()
    this.sectionObserver?.disconnect()
  }

  /**
   * El asset es contenido propio versionado en el repo, no entrada de usuario: por eso se
   * confía explícitamente en él. Si algún día el HTML viniera de la API o de una carga,
   * esta línea deja de ser aceptable.
   */
  private buildToc(html: string): TocEntry[] {
    const parsed = new DOMParser().parseFromString(html, 'text/html')
    return Array.from(parsed.querySelectorAll('h2[id]')).map((heading) => {
      //* El h2 puede llevar el chip de la decisión pendiente (`<span class="h">H5</span>`);
      //* en el índice sobra, así que se lee el título sin él.
      const clone = heading.cloneNode(true) as HTMLElement
      clone.querySelectorAll('.h').forEach((chip) => chip.remove())
      return { id: heading.id, label: (clone.textContent ?? '').replace(/\s+/g, ' ').trim() }
    })
  }

  private onContentRendered(): void {
    const content = this.contentRef?.nativeElement
    if (!content) return

    this.blockIndex.clear()
    this.blockOrder.clear()
    content.querySelectorAll<HTMLElement>('[data-block]').forEach((el) => {
      const id = blockIdOf(el)
      if (!id) return
      this.blockIndex.set(id, el)
      this.blockOrder.set(id, this.blockOrder.size)
    })

    this.describeLinks(content)
    this.rebuildBadges()
    this.observeContent()
    this.observeSections()
  }

  /**
   * Las referencias cruzadas del manual (§5, H6, Anexo A) son enlaces a un ancla del
   * propio documento. Sin interceptarlas, el fragmento se lo lleva el router de Angular y
   * el usuario acaba en el dashboard con una URL que no significa nada.
   *
   * Tres destinos posibles, en este orden: `data-app` → una pantalla de la app; `data-doc` → otro
   * manual, conservando la sección; y si no, una sección del documento actual.
   */
  private onContentClick(event: MouseEvent): void {
    const link = (event.target as HTMLElement)?.closest?.('a[href^="#"], a[data-app]') as HTMLAnchorElement | null
    if (!link) return

    event.preventDefault()
    const seccion = link.getAttribute('href')?.slice(1) ?? ''
    const otroDoc = link.dataset['doc']

    //* `data-app` lleva a una pantalla de la app, no a otro manual. Se navega con el router en vez
    //* de dejar que el `href` haga su trabajo: un enlace normal provocaría RECARGA COMPLETA de la
    //* SPA —sesión, estado y todo—, que es justo lo que no queremos cuando alguien viene del manual
    //* a mirar una pantalla. El `href` se conserva en el asset como respaldo: si algo impidiera que
    //* este listener corriera, el enlace seguiría llevando al sitio correcto, solo que recargando.
    const rutaApp = link.dataset['app']
    if (rutaApp) {
      this.router.navigateByUrl(rutaApp)
      return
    }

    //* Una referencia puede apuntar a otro manual. Se navega conservando la sección, así
    //* que el lector aterriza en el punto exacto del que se le habló.
    if (otroDoc && otroDoc !== this.doc?.slug) {
      this.router.navigate(['/documentation', otroDoc], { fragment: seccion || undefined })
      return
    }

    this.goToSection(seccion)
  }

  /** Salto explícito: sí deja entrada en el historial, para que «atrás» regrese. */
  private goToSection(id: string): void {
    if (!id) return
    this.ultimoFragmentoEscrito = id
    this.router.navigate([], { relativeTo: this.route, fragment: id })
    this.jumpTo(id)
  }

  private setActiveSection(id: string): void {
    if (this.activeSection === id) return
    this.activeSection = id

    //* `replaceUrl` a propósito: el índice se mueve con cada scroll y llenar el historial
    //* con cada sección dejaría el botón «atrás» inservible.
    this.ultimoFragmentoEscrito = id
    this.router.navigate([], { relativeTo: this.route, fragment: id, replaceUrl: true })
  }

  /** Conmuta entre el manual y su guía de plataforma conservando la sección. */
  goToCompanion(): void {
    if (!this.companion) return

    //* El fragmento manda sobre `activeSection`: al pulsar el índice, la URL ya trae la
    //* sección mientras el scroll-spy todavía viene en camino, y sin esto el conmutador
    //* aterrizaba en la sección anterior.
    const seccion = this.route.snapshot.fragment || this.activeSection
    this.router.navigate(['/documentation', this.companion.slug], { fragment: seccion || undefined })
  }

  /** Devuelve `false` si el documento no tiene esa sección, para que el llamador reaccione. */
  private jumpTo(id: string): boolean {
    const target = id ? this.contentRef?.nativeElement.querySelector<HTMLElement>(`#${CSS.escape(id)}`) : null
    if (!target) return false

    this.scrollToElement(target)

    //* Un destello al llegar: en un manual largo, sin él no queda claro dónde aterrizaste.
    target.classList.remove('is-target')
    void target.offsetWidth // reinicia la animación si se pulsa el mismo enlace dos veces
    target.classList.add('is-target')
    setTimeout(() => target.classList.remove('is-target'), 1600)
    return true
  }

  /** Al pasar el cursor, el enlace dice a dónde lleva en vez de mostrar sólo "§5". */
  private describeLinks(content: HTMLElement): void {
    content.querySelectorAll<HTMLAnchorElement>('a[href^="#"]').forEach((link) => {
      const id = link.getAttribute('href')?.slice(1) ?? ''
      const target = id ? content.querySelector<HTMLElement>(`#${CSS.escape(id)}`) : null

      const otroDoc = link.dataset['doc']
      if (otroDoc) {
        const destino = findDocumentation(otroDoc)
        link.classList.add('is-cross')
        link.title = destino ? `Ir a: ${destino.title}` : 'Este documento ya no existe'
        if (!destino) link.classList.add('is-broken')
        return
      }

      if (!target) {
        link.classList.add('is-broken')
        link.title = 'Esta referencia ya no existe en el documento'
        return
      }

      link.title = `Ir a: ${labelOfTarget(target)}`
    })
  }

  //* El contenido cambia de ancho durante toda la transición del panel. Un ResizeObserver
  //* recoloca las insignias en cada cuadro, así que siguen al bloque mientras se anima
  //* en vez de saltar al final.
  private observeContent(): void {
    this.resizeObserver?.disconnect()
    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => {
        this.positionOverlays()
        this.alignThreads()
      })
      this.resizeObserver.observe(this.contentRef.nativeElement)
    })
  }

  private observeSections(): void {
    this.sectionObserver?.disconnect()
    const headings = this.contentRef.nativeElement.querySelectorAll('h2[id]')
    if (!headings.length) return

    this.sectionObserver = new IntersectionObserver(
      (entries) => {
        //* El orden de `entries` no es el del documento: sin ordenar por posición, dos
        //* títulos dentro de la banda dejaban activo el de abajo.
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (!visible.length) return
        this.zone.run(() => this.setActiveSection(visible[0].target.id))
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 }
    )
    headings.forEach((heading) => this.sectionObserver?.observe(heading))
  }

  // ─────────────────────────── canaleta ───────────────────────────

  private onHover(event: MouseEvent): void {
    const target = event.target as HTMLElement

    //* Al ir del bloque al botón el cursor cruza la canaleta, que no es ningún bloque. Sin
    //* esta guarda el botón se escondía justo antes de poder hacerle clic.
    if (this.hoverBtn && (target === this.hoverBtn || this.hoverBtn.contains(target))) {
      this.cancelHide()
      return
    }

    const block = target?.closest?.('[data-block]') as HTMLElement | null
    if (block === this.hoverBlock) return

    this.hoverBlock?.classList.remove('is-hovered')
    this.hoverBlock = block

    if (!block) {
      this.scheduleHide()
      return
    }

    this.cancelHide()
    block.classList.add('is-hovered')

    //* Con hilo abierto la insignia ya ocupa la canaleta; para agregar otro hilo al mismo
    //* bloque está la acción del panel.
    const blockId = blockIdOf(block)
    if (blockId && this.badges.has(blockId)) {
      this.hideHoverButton()
      return
    }

    this.showHoverButton(block)
  }

  private clearHover(): void {
    this.hoverBlock?.classList.remove('is-hovered')
    this.hoverBlock = null
    this.cancelHide()
    this.hideHoverButton()
  }

  /** Margen para que el cursor alcance el botón tras salir del bloque. */
  private scheduleHide(): void {
    this.cancelHide()
    this.hideTimer = setTimeout(() => this.hideHoverButton(), 260)
  }

  private cancelHide(): void {
    if (this.hideTimer) clearTimeout(this.hideTimer)
    this.hideTimer = null
  }

  private showHoverButton(block: HTMLElement): void {
    if (!this.hoverBtn) this.hoverBtn = this.createHoverButton()
    this.hoverBtnBlockId = blockIdOf(block)
    this.renderer.setStyle(this.hoverBtn, 'top', `${this.topOf(block)}px`)
    this.renderer.addClass(this.hoverBtn, 'is-visible')
  }

  private hideHoverButton(): void {
    if (this.hoverBtn) this.renderer.removeClass(this.hoverBtn, 'is-visible')
  }

  private createHoverButton(): HTMLElement {
    const button = this.renderer.createElement('button')
    this.renderer.addClass(button, 'doc-gutter-btn')
    this.renderer.setAttribute(button, 'type', 'button')
    this.renderer.setAttribute(button, 'title', 'Comentar')
    this.renderer.setProperty(button, 'innerHTML', '<i class="fa-regular fa-comment-dots"></i>')
    this.renderer.listen(button, 'click', () => this.zone.run(() => this.openBlock(this.hoverBtnBlockId, true)))
    this.renderer.appendChild(this.shellRef.nativeElement, button)
    return button
  }

  private rebuildBadges(): void {
    this.badges.forEach((el) => this.renderer.removeChild(this.shellRef.nativeElement, el))
    this.badges.clear()

    //* El bucle sólo recorre bloques QUE TIENEN hilos, así que el que se queda sin ninguno
    //* nunca se visita: sin este barrido conserva la barra señalando una conversación borrada.
    this.blockIndex.forEach((el) => el.classList.remove('has-threads'))

    for (const blockId of this.blockIdsWithThreads()) {
      const block = this.blockIndex.get(blockId)
      if (!block) continue

      const ofBlock = this.threadsOfBlock(blockId)
      const total = ofBlock.reduce((acc, thread) => acc + thread.comments.length, 0)
      const allResolved = ofBlock.every((thread) => thread.status === 'RESOLVED')

      block.classList.toggle('has-threads', !allResolved)

      const badge = this.renderer.createElement('button')
      this.renderer.addClass(badge, 'doc-gutter-badge')
      if (allResolved) this.renderer.addClass(badge, 'is-resolved')
      this.renderer.setAttribute(badge, 'type', 'button')
      this.renderer.setAttribute(badge, 'title', allResolved ? 'Conversación resuelta' : 'Ver conversación')
      this.renderer.setProperty(badge, 'innerHTML', `<i class="fa-solid fa-comment"></i>${total}`)
      this.renderer.listen(badge, 'click', () => this.zone.run(() => this.openBlock(blockId, false)))
      this.renderer.appendChild(this.shellRef.nativeElement, badge)

      this.badges.set(blockId, badge)
    }

    this.rebuildDecisionChips()
    this.positionOverlays()
  }

  /**
   * Marca cada tarjeta de decisión con en qué punto está. El distintivo se inyecta en su fila
   * de título —que es flex con el título creciendo, así que cae solo a la derecha— y lleva el
   * clic que abre esa conversación: desde el distintivo se contesta sin buscar el bloque.
   */
  private rebuildDecisionChips(): void {
    this.decisionChips.forEach((el) => el.remove())
    this.decisionChips.clear()

    for (const decision of this.decisions) {
      const card = this.blockIndex.get(decision.blockId)
      const top = card?.querySelector('.top')
      if (!top) continue

      const estado = decisionState(this.threads, decision.blockId)
      const chip = this.renderer.createElement('button')
      this.renderer.addClass(chip, 'doc-dec-chip')
      this.renderer.addClass(chip, `is-${estado}`)
      this.renderer.setAttribute(chip, 'type', 'button')
      this.renderer.setAttribute(chip, 'title', estado === 'pending' ? 'Dejar la respuesta como comentario' : 'Ver la conversación')
      this.renderer.setProperty(chip, 'textContent', DECISION_STATE_LABEL[estado])
      this.renderer.listen(chip, 'click', () => this.zone.run(() => this.openBlock(decision.blockId, false)))
      this.renderer.appendChild(top, chip)

      this.decisionChips.set(decision.blockId, chip)
    }
  }

  //* Se leen TODAS las posiciones antes de escribir ninguna: intercalar lecturas y
  //* escrituras fuerza un reflujo por insignia en cada cuadro de la animación.
  private positionOverlays(): void {
    if (!this.shellRef) return
    const shellTop = this.shellRef.nativeElement.getBoundingClientRect().top

    const tops: Array<[HTMLElement, number | null]> = []
    this.badges.forEach((badge, blockId) => {
      const block = this.blockIndex.get(blockId)
      tops.push([badge, block ? block.getBoundingClientRect().top - shellTop : null])
    })

    for (const [badge, top] of tops) {
      if (top === null) {
        this.renderer.setStyle(badge, 'display', 'none')
        continue
      }
      this.renderer.setStyle(badge, 'display', '')
      this.renderer.setStyle(badge, 'top', `${top}px`)
    }
  }

  /**
   * Coloca cada conversación a la altura del bloque que comenta, como en un margen de
   * revisión. Si dos se encimarían, la segunda baja lo justo: nunca sube por encima de su
   * bloque, así que el orden vertical siempre coincide con el del documento.
   */
  private alignThreads(): void {
    const body = this.panelBodyRef?.nativeElement
    if (!body) return

    //* Bajo 1200px el panel es un overlay flotante: alinear con el documento dejaría las
    //* tarjetas a miles de píxeles de scroll dentro del propio panel.
    if (!this.panelOpen || window.innerWidth <= 1200) {
      this.renderer.removeStyle(body, 'min-height')
      return
    }

    const cards = this.cardRefs?.toArray().map((ref) => ref.nativeElement) ?? []
    if (!cards.length) {
      //* Sin tarjetas hay que soltar el alto anterior: si no, el filtro sin resultados deja
      //* miles de píxeles en blanco debajo del mensaje.
      this.renderer.removeStyle(body, 'min-height')
      return
    }

    const bodyTop = body.getBoundingClientRect().top

    //* Primero se lee TODO y luego se escribe: intercalar medidas y estilos fuerza un
    //* reflujo por tarjeta.
    const items = cards.map((el) => {
      const anchor = el.dataset['anchor']
      const block = anchor ? this.blockIndex.get(anchor) : null
      return {
        el,
        wanted: block ? block.getBoundingClientRect().top - bodyTop : null,
        height: el.offsetHeight,
      }
    })

    let cursor = 0
    for (const item of items) {
      const top = Math.max(item.wanted ?? cursor, cursor)
      this.renderer.setStyle(item.el, 'top', `${top}px`)
      cursor = top + item.height + 12
    }

    this.renderer.setStyle(body, 'min-height', `${cursor + 24}px`)
  }

  private topOf(block: HTMLElement): number {
    return block.getBoundingClientRect().top - this.shellRef.nativeElement.getBoundingClientRect().top
  }

  // ─────────────────────────── panel ───────────────────────────

  openBlock(blockId: string | null | undefined, compose: boolean): void {
    if (!blockId) return

    this.activeBlockId = blockId
    this.activeBlockText = this.textOf(blockId)
    this.panelMode = 'block'
    this.draft = ''
    this.highlight(blockId)
    this.openPanel()
    if (compose) setTimeout(() => document.getElementById('doc-new-comment')?.focus(), 280)
  }

  openAll(): void {
    this.activeBlockId = null
    this.panelMode = 'all'
    this.filter = 'open'
    this.highlight(null)
    this.openPanel()
  }

  private openPanel(): void {
    if (this.panelOpen) {
      this.positionOverlays()
      this.alignThreads()
      return
    }
    this.panelOpen = true
    if (this.tocOpen) {
      this.tocOpen = false
      this.tocAutoHidden = true
    }
  }

  closePanel(): void {
    this.panelOpen = false
    this.activeBlockId = null
    this.highlight(null)
    if (this.tocAutoHidden) {
      this.tocOpen = true
      this.tocAutoHidden = false
    }
  }

  toggleToc(): void {
    this.tocOpen = !this.tocOpen
    this.tocAutoHidden = false
  }

  setFilter(filter: ThreadFilter): void {
    this.filter = filter
  }

  scrollTo(id: string): void {
    this.goToSection(id)
  }

  focusBlock(blockId: string): void {
    const block = this.blockIndex.get(blockId)
    if (!block) return
    this.highlight(blockId)
    this.scrollToElement(block, true)
  }

  /**
   * Salta a un elemento del manual descontando el alto de la cabecera fija.
   *
   * No usa `scrollIntoView` ni `behavior: 'smooth'`: en este contenedor el scroll suave
   * NATIVO no mueve nada —medido: con `smooth` se queda en 0 y con `auto` sí viaja—, que
   * era la causa de que los enlaces funcionaran de forma intermitente. El recorrido se
   * anima a mano, así que no depende de que el navegador lo honre.
   */
  private scrollToElement(target: HTMLElement, centrar = false): void {
    const scroller = scrollParentOf(target)
    const margen = centrar ? Math.max(0, (scroller.clientHeight - target.offsetHeight) / 2) : HEAD_OFFSET
    const destino = target.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - margen

    this.animateScroll(scroller, Math.max(0, destino))
  }

  private animateScroll(scroller: HTMLElement, destino: number): void {
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame)

    const origen = scroller.scrollTop
    const distancia = destino - origen

    //* Quien pidió menos movimiento no quiere ver un recorrido de miles de píxeles.
    if (!distancia || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      scroller.scrollTop = destino
      return
    }

    const inicio = performance.now()
    const duracion = Math.min(600, Math.max(240, Math.abs(distancia) / 6))

    const paso = (ahora: number) => {
      const avance = Math.min(1, (ahora - inicio) / duracion)
      const suavizado = 1 - Math.pow(1 - avance, 3) // ease-out
      scroller.scrollTop = origen + distancia * suavizado
      this.scrollFrame = avance < 1 ? requestAnimationFrame(paso) : null
    }

    this.zone.runOutsideAngular(() => (this.scrollFrame = requestAnimationFrame(paso)))
  }

  private highlight(blockId: string | null): void {
    this.blockIndex.forEach((el) => el.classList.remove('is-active'))
    if (blockId) this.blockIndex.get(blockId)?.classList.add('is-active')
  }

  private textOf(blockId: string): string {
    const text = (this.blockIndex.get(blockId)?.innerText ?? '').replace(/\s+/g, ' ').trim()
    return text.length > 220 ? `${text.slice(0, 220)}…` : text
  }

  // ─────────────────────────── datos derivados ───────────────────────────

  private pasaFiltro(thread: DocumentationThread): boolean {
    if (this.filter === 'open') return thread.status === 'OPEN'
    if (this.filter === 'resolved') return thread.status === 'RESOLVED'
    //* «Decisiones»: no filtra por estado sino por QUÉ se comentó. Es el flujo del revisor
    //* —«enséñame solo lo que tengo que contestar»— y por eso incluye las ya cerradas.
    return this.decisionBlockIds.has(thread.blockId)
  }

  private get decisionBlockIds(): Set<string> {
    return new Set(this.decisions.map((decision) => decision.blockId))
  }

  /** Los dos marcadores: `answered` es la meta de Contabilidad, `closed` la nuestra. */
  get progress(): DecisionProgress {
    return decisionProgress(this.decisions, this.threads)
  }

  /** Abre el panel mostrando solo las decisiones. Lo dispara el contador de la cabecera. */
  openDecisions(): void {
    this.activeBlockId = null
    this.panelMode = 'all'
    this.filter = 'decisions'
    this.highlight(null)
    this.openPanel()
  }

  get visibleThreads(): DocumentationThread[] {
    const base = this.panelMode === 'block' ? this.threadsOfBlock(this.activeBlockId) : this.threads.filter((thread) => this.blockIndex.has(thread.blockId))
    const filtered = this.panelMode === 'block' || this.filter === 'all' ? base : base.filter((thread) => this.pasaFiltro(thread))

    //* Por posición en el documento, no por fecha: la columna de comentarios acompaña la
    //* lectura. Dentro del mismo bloque, el más antiguo primero.
    return [...filtered].sort((a, b) => {
      const orden = (this.blockOrder.get(a.blockId) ?? 0) - (this.blockOrder.get(b.blockId) ?? 0)
      return orden !== 0 ? orden : a.createdAt.localeCompare(b.createdAt)
    })
  }

  /** Hilos cuyo bloque ya no existe en el documento: se conservan con su texto anclado. */
  get orphanThreads(): DocumentationThread[] {
    if (this.panelMode === 'block') return []
    return this.threads.filter((thread) => !this.blockIndex.has(thread.blockId))
  }

  get openCount(): number {
    return this.threads.filter((thread) => thread.status === 'OPEN').length
  }

  threadsOfBlock(blockId: string | null): DocumentationThread[] {
    return blockId ? this.threads.filter((thread) => thread.blockId === blockId) : []
  }

  private blockIdsWithThreads(): string[] {
    return Array.from(new Set(this.threads.map((thread) => thread.blockId)))
  }

  get canComment(): boolean {
    return this.authService.hasModulePermission('documentation', 'comment')
  }

  get canResolve(): boolean {
    return this.authService.hasModulePermission('documentation', 'resolve')
  }

  isMine(authorId?: string): boolean {
    return !!authorId && authorId === this.authService.getUser?.user?.id
  }

  // ─────────────────────────── acciones ───────────────────────────

  createThread(): void {
    const content = this.draft.trim()
    if (!content || !this.activeBlockId || !this.doc || this.saving) return

    this.saving = true
    this.documentationService
      .createThread({
        docSlug: this.doc!.slug,
        blockId: this.activeBlockId,
        anchorText: this.activeBlockText,
        content,
      })
      .subscribe((res) => this.afterWrite(res, 'Comentario publicado', () => (this.draft = '')))
  }

  reply(thread: DocumentationThread): void {
    const content = (this.replyDraft[thread._id] ?? '').trim()
    if (!content || this.saving) return

    this.saving = true
    this.documentationService.addComment(thread._id, content).subscribe((res) => this.afterWrite(res, 'Respuesta publicada', () => delete this.replyDraft[thread._id]))
  }

  startEdit(commentId: string, content: string): void {
    this.editingId = commentId
    this.editDraft = content
  }

  cancelEdit(): void {
    this.editingId = null
    this.editDraft = ''
  }

  saveEdit(): void {
    const content = this.editDraft.trim()
    if (!content || !this.editingId || this.saving) return

    this.saving = true
    this.documentationService.updateComment(this.editingId, content).subscribe((res) => this.afterWrite(res, 'Comentario actualizado', () => this.cancelEdit()))
  }

  removeComment(commentId: string): void {
    if (this.saving) return

    this.saving = true
    this.documentationService.deleteComment(commentId).subscribe((res) => this.afterWrite(res, 'Comentario eliminado'))
  }

  toggleResolve(thread: DocumentationThread): void {
    if (this.saving) return

    const status: ThreadStatus = thread.status === 'RESOLVED' ? 'OPEN' : 'RESOLVED'
    this.saving = true
    this.documentationService.setStatus(thread._id, status).subscribe((res) => this.afterWrite(res, status === 'RESOLVED' ? 'Conversación resuelta' : 'Conversación reabierta'))
  }

  //* Toda escritura devuelve el hilo desde el backend, pero se recarga la lista completa:
  //* es una sola consulta y evita que la UI reconstruya un estado que ya calculó el servidor.
  private afterWrite(res: any, okMessage: string, onOk?: () => void): void {
    this.saving = false

    if (res?.error) {
      this.messageService.add({ severity: 'error', summary: 'No se pudo guardar', detail: res.msg || 'Intenta de nuevo', life: 4000 })
      return
    }

    onOk?.()
    this.messageService.add({ severity: 'success', summary: okMessage, life: 2500 })
    this.refreshThreads()
  }

  private refreshThreads(): void {
    if (!this.doc) return
    //* Si el refresco falla se conservan los hilos que ya había. Vaciarlos haría creer que
    //* los comentarios se perdieron —incluidos los ajenos— cuando sólo falló una lectura.
    this.documentationService
      .getThreads(this.doc.slug)
      .pipe(catchError(() => of(null)))
      .subscribe((res) => {
        if (!Array.isArray(res?.data)) {
          this.threadsError = true
          return
        }
        this.threadsError = false
        this.threads = res.data
        this.rebuildBadges()
        //* Responder no cambia el número de tarjetas, así que `cardRefs.changes` no emite:
        //* sin esto la tarjeta que creció tapa a la de abajo.
        setTimeout(() => this.alignThreads())
        if (this.activeBlockId) this.highlight(this.activeBlockId)
      })
  }

  print(): void {
    window.print()
  }

  // ─────────────────────────── presentación ───────────────────────────

  authorName(author: any): string {
    if (!author) return 'Usuario'
    const names = typeof author.names === 'string' ? author.names : ''
    const lastNames = typeof author.lastNames === 'string' ? author.lastNames : ''
    return `${names} ${lastNames}`.trim() || author.email || 'Usuario'
  }

  authorRole(author: any): string {
    const role = author?.role
    return typeof role === 'object' && role ? role.name : ''
  }

  initials(author: any): string {
    return this.authorName(author)
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((word: string) => word[0])
      .join('')
      .toUpperCase()
  }

  /** Color estable por autor: mismo usuario, mismo color, sin guardarlo en la base. */
  avatarColor(author: any): string {
    const palette = ['#3B82F6', '#00C781', '#a855f7', '#f97316', '#d21313', '#0ea5e9', '#7E57C2']
    const seed = (author?._id ?? this.authorName(author)) as string
    let hash = 0
    for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
    return palette[hash % palette.length]
  }

  ago(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime()
    const min = 60000
    if (diff < min) return 'hace un momento'
    if (diff < 60 * min) return `hace ${Math.round(diff / min)} min`
    if (diff < 24 * 60 * min) return `hace ${Math.round(diff / (60 * min))} h`
    if (diff < 7 * 24 * 60 * min) return `hace ${Math.round(diff / (24 * 60 * min))} d`
    return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
  }

  exact(iso: string): string {
    return new Date(iso).toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' })
  }

  //* Anchos dispares a propósito: un índice con todas las líneas iguales se lee como
  //* una tabla, no como un texto cargando.
  readonly skeletonToc = [78, 58, 86, 66, 92, 72, 62, 88, 54, 80, 68, 84]
  readonly skeletonCards = [1, 2, 3, 4]
  readonly skeletonRows = [1, 2, 3, 4]

  trackByThread = (_: number, thread: DocumentationThread) => thread._id
  trackByComment = (_: number, comment: { _id: string }) => comment._id
  trackByToc = (_: number, entry: TocEntry) => entry.id
}

/** `dataset` es una index signature: acceso por corchetes y sin dar por hecho el valor. */
function blockIdOf(el: HTMLElement): string | null {
  return el.dataset['block'] ?? null
}

/** Texto legible del destino de una referencia: el título de la sección o de la decisión. */
function labelOfTarget(el: HTMLElement): string {
  const clone = el.cloneNode(true) as HTMLElement
  clone.querySelectorAll('.h').forEach((chip) => chip.remove())

  //* En una tarjeta de decisión el título vive en `.ti`; usar su texto completo traería
  //* también la pregunta y las tres opciones.
  const titulo = clone.querySelector('.ti')
  const crudo = titulo ? `${clone.querySelector('.id')?.textContent ?? ''} ${titulo.textContent ?? ''}` : (clone.textContent ?? '')

  const limpio = crudo.replace(/\s+/g, ' ').trim()
  return limpio.length > 90 ? `${limpio.slice(0, 90)}…` : limpio
}

/** Contenedor que de verdad hace scroll: en el panel es el `main` del shell. */
function scrollParentOf(el: HTMLElement): HTMLElement {
  let node = el.parentElement
  while (node) {
    const overflow = getComputedStyle(node).overflowY
    if ((overflow === 'auto' || overflow === 'scroll') && node.scrollHeight > node.clientHeight) return node
    node = node.parentElement
  }
  return document.scrollingElement as HTMLElement
}
